import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api, setAuthToken, getAuthToken } from '../services/api';

interface GalleryItem {
  id: number;
  title: string;
  description: string;
  category?: string;
  location?: string;
  beforeImage: string;
  afterImage: string;
  beforeThumbnail?: string;
  afterThumbnail?: string;
  createdAt: string;
  updatedAt: string;
}

interface UploadedFile {
  original: string;
  thumbnail: string;
  filename: string;
  size: number;
}

interface TabProps {
  formData: any;
  setFormData: (data: any) => void;
  editingItem: GalleryItem | null;
  handleSubmit: (e: React.FormEvent) => void;
  handleFileUpload: (files: FileList) => void;
  resetForm: () => void;
  galleryItems: GalleryItem[];
  handleEdit: (item: GalleryItem) => void;
  handleDelete: (id: number) => void;
  loading: boolean;
}

// Komponent zakładki Galeria
const GalleryTab: React.FC<TabProps> = ({
  formData,
  setFormData,
  editingItem,
  handleSubmit,
  handleFileUpload,
  resetForm,
  galleryItems,
  handleEdit,
  handleDelete,
  loading
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Form */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">
          {editingItem ? 'Edytuj projekt' : 'Dodaj nowy projekt do galerii'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tytuł *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opis
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategoria
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="np. Ogród zimowy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lokalizacja
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="np. Poznań"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload plików
            </label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              Wybierz obrazy do przesłania (max 10MB każdy)
            </p>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Zapisywanie...' : editingItem ? 'Zaktualizuj' : 'Dodaj do galerii'}
            </button>
            
            {editingItem && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Anuluj
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Gallery Items List */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">
          Projekty w galerii ({galleryItems.length})
        </h2>
        
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {galleryItems.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-900">{item.title}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edytuj
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Usuń
                  </button>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mb-2">{item.description}</p>
              
              <div className="flex gap-2 text-xs text-gray-500">
                {item.category && (
                  <span className="bg-gray-100 px-2 py-1 rounded">{item.category}</span>
                )}
                {item.location && (
                  <span className="bg-gray-100 px-2 py-1 rounded">{item.location}</span>
                )}
              </div>
            </div>
          ))}
          
          {galleryItems.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              Brak projektów w galerii
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Komponent zakładki Transformacje
const TransformationsTab: React.FC<TabProps> = ({
  formData,
  setFormData,
  editingItem,
  handleSubmit,
  resetForm,
  galleryItems,
  handleEdit,
  handleDelete,
  loading
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [selectedBeforeImage, setSelectedBeforeImage] = useState<string>('');
  const [selectedAfterImage, setSelectedAfterImage] = useState<string>('');

  // Handle file upload with state update
  const handleTransformationFileUpload = async (files: FileList) => {
    if (files.length === 0) return;

    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('images', file);
      });

      const response = await api.uploadImages(formData);
      const newFiles: UploadedFile[] = response.data.files;
      
      setUploadedFiles(prev => [...prev, ...newFiles]);
    } catch (err: any) {
      console.error('Upload error:', err);
    }
  };

  // Handle submit with selected images
  const handleTransformationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !selectedBeforeImage || !selectedAfterImage) {
      alert('Wypełnij tytuł i wybierz zdjęcia przed i po');
      return;
    }

    // Call the original handleSubmit
    await handleSubmit(e);
  };

  // Update form data when images are selected
  useEffect(() => {
    setFormData((prev: any) => ({
      ...prev,
      beforeImage: selectedBeforeImage,
      afterImage: selectedAfterImage,
    }));
  }, [selectedBeforeImage, selectedAfterImage, setFormData]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Form */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">
          {editingItem ? 'Edytuj transformację' : 'Dodaj nową transformację przed/po'}
        </h2>
        
        <form onSubmit={handleTransformationSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tytuł transformacji *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="np. Ogród zimowy - transformacja"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opis transformacji
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Opisz proces transformacji, użyte materiały, efekty..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategoria
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Wybierz kategorię</option>
                <option value="Ogród zimowy">Ogród zimowy</option>
                <option value="Zabudowa tarasu">Zabudowa tarasu</option>
                <option value="Przeszklenie balkonowe">Przeszklenie balkonowe</option>
                <option value="System bezramowy">System bezramowy</option>
                <option value="Inne">Inne</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lokalizacja
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="np. Poznań"
              />
            </div>
          </div>

          {/* Upload Section */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              1. Prześlij zdjęcia transformacji
            </label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => e.target.files && handleTransformationFileUpload(e.target.files)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              Prześlij zdjęcia przed i po transformacji (max 10MB każdy)
            </p>
          </div>

          {/* Image Selection */}
          {uploadedFiles.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                2. Wybierz zdjęcia przed i po
              </h3>
              
              {/* Uploaded images grid */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="relative">
                    <img
                      src={`http://localhost:6544${file.original}`}
                      alt={`Przesłane zdjęcie ${index + 1}`}
                      className="w-full h-20 object-cover rounded border"
                    />
                    <div className="absolute top-1 right-1 bg-white rounded px-1 text-xs">
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>

              {/* Before image selection */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zdjęcie PRZED *
                </label>
                <select
                  value={selectedBeforeImage}
                  onChange={(e) => setSelectedBeforeImage(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Wybierz zdjęcie przed transformacją</option>
                  {uploadedFiles.map((file, index) => (
                    <option key={index} value={`http://localhost:6544${file.original}`}>
                      Zdjęcie {index + 1} ({file.filename})
                    </option>
                  ))}
                </select>
              </div>

              {/* After image selection */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zdjęcie PO *
                </label>
                <select
                  value={selectedAfterImage}
                  onChange={(e) => setSelectedAfterImage(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Wybierz zdjęcie po transformacji</option>
                  {uploadedFiles.map((file, index) => (
                    <option key={index} value={`http://localhost:6544${file.original}`}>
                      Zdjęcie {index + 1} ({file.filename})
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview selected images */}
              {(selectedBeforeImage || selectedAfterImage) && (
                <div className="grid grid-cols-2 gap-4 mt-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Wybrane zdjęcie PRZED:</p>
                    {selectedBeforeImage && (
                      <img
                        src={selectedBeforeImage}
                        alt="Wybrane zdjęcie przed"
                        className="w-full h-16 object-cover rounded border"
                      />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Wybrane zdjęcie PO:</p>
                    {selectedAfterImage && (
                      <img
                        src={selectedAfterImage}
                        alt="Wybrane zdjęcie po"
                        className="w-full h-16 object-cover rounded border"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading || !selectedBeforeImage || !selectedAfterImage}
              className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Zapisywanie...' : editingItem ? 'Zaktualizuj transformację' : 'Dodaj transformację'}
            </button>
            
            {editingItem && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Anuluj
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Transformations List */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">
          Transformacje przed/po ({galleryItems.length})
        </h2>
        
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {galleryItems.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-900">{item.title}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edytuj
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Usuń
                  </button>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mb-2">{item.description}</p>
              
              <div className="flex gap-2 text-xs text-gray-500 mb-3">
                {item.category && (
                  <span className="bg-gray-100 px-2 py-1 rounded">{item.category}</span>
                )}
                {item.location && (
                  <span className="bg-gray-100 px-2 py-1 rounded">{item.location}</span>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">PRZED</p>
                  <img
                    src={item.beforeImage}
                    alt="Przed transformacją"
                    className="w-full h-24 object-cover rounded border"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">PO</p>
                  <img
                    src={item.afterImage}
                    alt="Po transformacji"
                    className="w-full h-24 object-cover rounded border"
                  />
                </div>
              </div>
            </div>
          ))}
          
          {galleryItems.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              Brak transformacji
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const GalleryAdmin: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ login: '', password: '' });
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'gallery' | 'transformations'>('gallery');

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    beforeImage: '',
    afterImage: '',
  });

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          await api.checkAuth();
          setIsAuthenticated(true);
          loadGallery();
          loadStats();
        } catch {
          setAuthToken(null);
          setIsAuthenticated(false);
        }
      }
    };
    checkAuth();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.login(loginData);
      setAuthToken(response.data.token);
      setIsAuthenticated(true);
      setSuccess('Zalogowano pomyślnie!');
      loadGallery();
      loadStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd logowania');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    setAuthToken(null);
    setIsAuthenticated(false);
    setGalleryItems([]);
    setStats(null);
  };

  const loadGallery = async () => {
    try {
      const response = await api.getGallery();
      setGalleryItems(response.data);
    } catch (err: any) {
      setError('Błąd ładowania galerii');
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.getStats();
      setStats(response.data);
    } catch (err: any) {
      console.error('Error loading stats:', err);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('images', file);
      });

      const response = await api.uploadImages(formData);
      const uploadedFiles: UploadedFile[] = response.data.files;

      setSuccess(`Przesłano ${uploadedFiles.length} plików pomyślnie!`);
      
      // Display uploaded file URLs for easy copying
      uploadedFiles.forEach(file => {
        console.log(`Uploaded: ${file.original}`);
      });
      
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd przesyłania plików');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.beforeImage || !formData.afterImage) {
      setError('Wypełnij wymagane pola: tytuł, obraz przed i po');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingItem) {
        await api.updateGalleryItem(editingItem.id, formData);
        setSuccess('Projekt zaktualizowany pomyślnie!');
      } else {
        await api.addGalleryItem(formData);
        setSuccess('Projekt dodany pomyślnie!');
      }
      
      loadGallery();
      loadStats();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd zapisywania projektu');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: GalleryItem) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      category: item.category || '',
      location: item.location || '',
      beforeImage: item.beforeImage,
      afterImage: item.afterImage,
    });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten projekt?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.deleteGalleryItem(id);
      setSuccess('Projekt usunięty pomyślnie!');
      loadGallery();
      loadStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd usuwania projektu');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      location: '',
      beforeImage: '',
      afterImage: '',
    });
    setEditingItem(null);
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md"
        >
          <h1 className="text-2xl font-bold text-center mb-6">
            Panel Administracyjny
          </h1>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Login
              </label>
              <input
                type="text"
                value={loginData.login}
                onChange={(e) => setLoginData({...loginData, login: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hasło
              </label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Logowanie...' : 'Zaloguj się'}
            </button>
          </form>
          
          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Panel Administracyjny Galerii
          </h1>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Wyloguj
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Messages */}
        {(error || success) && (
          <div className="mb-6">
            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between items-center">
                <span>{error}</span>
                <button onClick={clearMessages} className="text-red-700 hover:text-red-900">
                  ×
                </button>
              </div>
            )}
            {success && (
              <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex justify-between items-center">
                <span>{success}</span>
                <button onClick={clearMessages} className="text-green-700 hover:text-green-900">
                  ×
                </button>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Łączna liczba projektów</h3>
              <p className="text-3xl font-bold text-blue-600">{stats.totalItems}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Kategorie</h3>
              <div className="space-y-1">
                {Object.entries(stats.categories).map(([category, count]) => (
                  <div key={category} className="flex justify-between">
                    <span className="text-gray-600">{category}</span>
                    <span className="font-semibold">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Ostatnio dodane</h3>
              <div className="space-y-1 text-sm">
                {stats.recentlyAdded.map((item: any) => (
                  <div key={item.id} className="text-gray-600">
                    {item.title}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('gallery')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'gallery'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Galeria Projektów
                </button>
                <button
                  onClick={() => setActiveTab('transformations')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'transformations'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Transformacje Przed/Po
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'gallery' ? (
          <GalleryTab
            formData={formData}
            setFormData={setFormData}
            editingItem={editingItem}
            handleSubmit={handleSubmit}
            handleFileUpload={handleFileUpload}
            resetForm={resetForm}
            galleryItems={galleryItems}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            loading={loading}
          />
        ) : (
          <TransformationsTab
            formData={formData}
            setFormData={setFormData}
            editingItem={editingItem}
            handleSubmit={handleSubmit}
            handleFileUpload={handleFileUpload}
            resetForm={resetForm}
            galleryItems={galleryItems}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

export default GalleryAdmin;
